/**
 * Particle Text Reveal Component
 *
 * A magical animation component that reveals text with particle effects.
 * Particles converge from random positions to form the text.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

// Inject keyframes for particle animations
const injectParticleStyles = () => {
  const styleId = "particle-reveal-styles";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    @keyframes particle-float {
      0%, 100% { transform: translateY(0) translateX(0); }
      25% { transform: translateY(-10px) translateX(5px); }
      50% { transform: translateY(-5px) translateX(-5px); }
      75% { transform: translateY(-15px) translateX(3px); }
    }
    
    @keyframes particle-glow {
      0%, 100% { opacity: 0.3; filter: blur(0px); }
      50% { opacity: 0.8; filter: blur(1px); }
    }
    
    @keyframes particle-converge {
      0% {
        opacity: 0;
        transform: translate(var(--start-x), var(--start-y)) scale(0.5);
        filter: blur(4px);
      }
      60% {
        opacity: 1;
        transform: translate(0, 0) scale(1.2);
        filter: blur(2px);
      }
      100% {
        opacity: 1;
        transform: translate(0, 0) scale(1);
        filter: blur(0px);
      }
    }
    
    @keyframes content-materialize {
      0% {
        opacity: 0;
        transform: translateY(10px);
        filter: blur(8px);
      }
      60% {
        opacity: 0.8;
        transform: translateY(2px);
        filter: blur(2px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
        filter: blur(0px);
      }
    }
    
    @keyframes glow-pulse {
      0%, 100% {
        box-shadow: 0 0 20px var(--glow-color, rgba(46, 189, 133, 0.3));
      }
      50% {
        box-shadow: 0 0 40px var(--glow-color, rgba(46, 189, 133, 0.5)), 
                    0 0 60px var(--glow-color, rgba(46, 189, 133, 0.2));
      }
    }
    
    @keyframes shimmer-scan {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }
    
    @keyframes sparkle {
      0%, 100% { opacity: 0; transform: scale(0); }
      50% { opacity: 1; transform: scale(1); }
    }
  `;
  document.head.appendChild(style);
};

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
}

interface ParticleTextRevealProps {
  children: ReactNode;
  /** Whether the content is being revealed */
  isRevealing: boolean;
  /** Animation delay for this section */
  delay?: number;
  /** Custom glow color */
  glowColor?: string;
  /** Show particles during reveal */
  showParticles?: boolean;
  /** Custom class name */
  className?: string;
  /** Callback when reveal animation completes */
  onRevealComplete?: () => void;
}

/**
 * Particle Text Reveal - Magical text materialization effect
 */
export function ParticleTextReveal({
  children,
  isRevealing,
  delay = 0,
  glowColor = "rgba(46, 189, 133, 0.4)",
  showParticles = true,
  className,
  onRevealComplete,
}: ParticleTextRevealProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  // Inject styles on mount
  useEffect(() => {
    injectParticleStyles();
  }, []);

  // Generate particles when revealing starts
  useEffect(() => {
    if (isRevealing && showParticles && !isComplete) {
      const newParticles: Particle[] = [];
      const numParticles = 20 + Math.floor(Math.random() * 15);

      for (let i = 0; i < numParticles; i++) {
        newParticles.push({
          id: i,
          x: (Math.random() - 0.5) * 300,
          y: (Math.random() - 0.5) * 200,
          size: 2 + Math.random() * 4,
          color: Math.random() > 0.5 ? "#2ebd85" : "#5fd4a4",
          delay: Math.random() * 0.5,
        });
      }

      setParticles(newParticles);

      // Clear particles after animation
      const timer = setTimeout(() => {
        setParticles([]);
        setIsComplete(true);
        onRevealComplete?.();
      }, 1500 + delay);

      return () => clearTimeout(timer);
    }
  }, [isRevealing, showParticles, delay, onRevealComplete, isComplete]);

  // Reset when not revealing
  useEffect(() => {
    if (!isRevealing) {
      setIsComplete(false);
      setParticles([]);
    }
  }, [isRevealing]);

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      style={{
        ["--glow-color" as string]: glowColor,
      }}
    >
      {/* Particles layer */}
      {showParticles && particles.length > 0 && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute rounded-full"
              style={{
                width: particle.size,
                height: particle.size,
                backgroundColor: particle.color,
                left: "50%",
                top: "50%",
                ["--start-x" as string]: `${particle.x}px`,
                ["--start-y" as string]: `${particle.y}px`,
                animation: `particle-converge 1s ease-out ${delay + particle.delay}s forwards`,
                opacity: 0,
                boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
              }}
            />
          ))}
        </div>
      )}

      {/* Content layer */}
      <div
        className={cn(
          "relative z-20",
          isRevealing && !isComplete && "opacity-0"
        )}
        style={
          isRevealing
            ? {
                animation: `content-materialize 0.8s ease-out ${delay + 0.3}s forwards`,
              }
            : undefined
        }
      >
        {children}
      </div>

      {/* Glow effect during reveal */}
      {isRevealing && !isComplete && (
        <div
          className="absolute inset-0 rounded-lg pointer-events-none z-0"
          style={{
            animation: `glow-pulse 1.5s ease-in-out ${delay}s infinite`,
          }}
        />
      )}
    </div>
  );
}

/**
 * Floating Particles Background
 * Use this for the idle state before generation
 */
interface FloatingParticlesProps {
  count?: number;
  className?: string;
}

export function FloatingParticles({
  count = 30,
  className,
}: FloatingParticlesProps) {
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    injectParticleStyles();

    const newParticles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 2 + Math.random() * 3,
        color: Math.random() > 0.5 ? "#2ebd85" : "#5fd4a4",
        delay: Math.random() * 3,
      });
    }
    setParticles(newParticles);
  }, [count]);

  return (
    <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            opacity: 0.4,
            animation: `particle-float ${3 + Math.random() * 2}s ease-in-out infinite, 
                        particle-glow ${2 + Math.random()}s ease-in-out infinite`,
            animationDelay: `${particle.delay}s`,
            boxShadow: `0 0 ${particle.size}px ${particle.color}`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Shimmer Loading Effect
 * Use this during API call
 */
interface ShimmerEffectProps {
  className?: string;
}

export function ShimmerEffect({ className }: ShimmerEffectProps) {
  useEffect(() => {
    injectParticleStyles();
  }, []);

  return (
    <div
      className={cn(
        "absolute inset-0 pointer-events-none",
        "bg-gradient-to-r from-transparent via-white/10 to-transparent",
        "bg-[length:200%_100%]",
        className
      )}
      style={{
        animation: "shimmer-scan 2s linear infinite",
      }}
    />
  );
}

/**
 * Sparkle Effect
 * Random sparkles that appear during reveal
 */
interface SparkleProps {
  x: number;
  y: number;
  delay: number;
  size?: number;
}

export function Sparkle({ x, y, delay, size = 4 }: SparkleProps) {
  useEffect(() => {
    injectParticleStyles();
  }, []);

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: size,
        height: size,
        animation: `sparkle 0.6s ease-in-out ${delay}s`,
      }}
    >
      <svg viewBox="0 0 24 24" fill="#2ebd85" className="w-full h-full">
        <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10L12 0Z" />
      </svg>
    </div>
  );
}

